/**
 * useSites Hook
 *
 * React Query hook for fetching sites with filtering and pagination
 */

import { useQuery } from '@tanstack/react-query';
import api from '../services/api';

export interface Site {
  site_id: string;
  name: string;
  site_code: string;
  org_unit_id: string | null;
  status: 'online' | 'offline' | 'degraded' | 'maintenance';
  capacity_mwh: number | string; // PostgreSQL numeric arrives as string; legacy single figure, fallback only
  battery_count: number;
  gen_capacity_kw?: number | null;   // total generator nameplate, kW (pre-aggregated in kpi_metadata.generator)
  generator_count?: number | null;   // generator unit count (kpi_metadata.generator)
  bess_power_mw?: number | null;     // BESS nameplate power, MW (kpi_metadata.battery_storage)
  bess_energy_mwh?: number | null;   // BESS nameplate energy, MWh (kpi_metadata.battery_storage)
  active_alarms: number;
  last_heartbeat: string | null;
  timezone: string;
  connection_status: 'connected' | 'degraded' | 'offline' | 'unknown';
  operational_mode: 'charging' | 'discharging' | 'idle' | 'standby';
  power_kw?: number;
  alarms_critical_unacked?: number | null;  // active + unacknowledged, from lateral join (#565)
  alarms_warning_unacked?: number | null;
  commissioning_status?: string | null;     // kpi_metadata.commissioning_status; 'commissioning' gates the card state
  location_label?: string | null;           // "City, ST" from config.sites.address; null when address is empty
}

export interface SitesFilters {
  portfolio_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

interface SitesResponse {
  success: boolean;
  data: {
    sites: Site[];
    pagination: {
      total: number;
      page: number;
      limit: number;
      total_pages: number;
    };
  };
}

export function useSites(filters: SitesFilters = {}) {
  const { portfolio_id, status, search, page = 1, limit = 50 } = filters;

  return useQuery({
    queryKey: ['sites', portfolio_id, status, search, page, limit],
    queryFn: async () => {
      const params = new URLSearchParams();

      if (portfolio_id) params.append('portfolio_id', portfolio_id);
      if (status) params.append('status', status);
      if (search) params.append('search', search);
      params.append('page', page.toString());
      params.append('limit', limit.toString());

      const response = await api.get<SitesResponse>(`/v1/sites?${params.toString()}`);
      return response.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 25000, // Consider data stale after 25 seconds
  });
}
